import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  Users,
  FileText,
  Route,
  ClipboardCheck,
  Brain,
  MessageCircle,
  HeartHandshake,
  Briefcase,
  Users2,
  Network,
  BookMarked,
  BookOpenCheck,
  Palette as ContentIcon,
  CalendarClock,
  GraduationCap,
  FlaskConical,
  FileStack,
  Banknote,
  Link2,
  ShoppingCart,
  Building,
  Workflow,
  Wallet,
  BarChart3,
  BarChart2,
  CheckCircle,
  AlertCircle,
  ReceiptText,
  ShieldCheck,
  Inbox,
  Scale,
  FileSearch,
  Settings as SettingsIcon,
  Sparkles,
  Bot,
  BookOpen,
  MessageSquareText,
  Activity,
  TrendingUp,
  Building2,
  Plug,
  Database,
  Zap,
  Rocket,
  Palette,
  X,
  ChevronDown,
  ChevronRight,
  Leaf,
  Upload,
  ScrollText,
  Award,
  ClipboardList,
  Layers,
  Target,
  UserCheck,
  SlidersHorizontal,
  BedDouble,
  Bell,
  Star,
  Calendar,
  CreditCard,
  Camera,
  CheckSquare,
} from "lucide-react";
import { useInstitution } from "../../context/InstitutionContext";
import { useAuth } from "../../context/AuthContext";
import { useTenantModules } from "../../lib/useTenantModules";
import { useTenantConfig } from "../../context/TenantConfigContext";

/**
 * AcademiaOS Enterprise Sidebar Navigation
 *
 * AOS-NAV-IMPLEMENT-01 | OWNER APPROVED | 2026-02
 * Implements the full 12-domain navigation architecture approved under AOS-NAV-FREEZE-01.
 *
 * CANONICAL 12 DOMAINS (immutable order):
 *   1.  Home & Work          2.  Academics
 *   3.  Student Lifecycle    4.  Assessment
 *   5.  Campus Services      6.  Operations
 *   7.  Finance              8.  People
 *   9.  Research            10.  Quality
 *  11.  Intelligence         12. Administration
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FROZEN ELEMENTS (AOS-NAV-FREEZE-01 — MUST NOT change without a superseding ADR):
 *
 *   Domain 5 "Campus Services"
 *   Group testid:  sidebar-group-campus
 *   Item label:    "Hostels & Residential Life"
 *   Route:         /campus/hostel
 *   Item testid:   sidebar-nav-hostel
 *   Module:        08.02  (canonical M5; legacy alias was HOSTEL — eliminated per AOS-SUB-03)
 *
 * REGRESSION GUARD: tests/test_nav_freeze_guard.py (AOS-NAV-IMPLEMENT-01 suite)
 * ─────────────────────────────────────────────────────────────────────────────
 */

const NAV_SECTIONS = [
  // ── 1. HOME & WORK ────────────────────────────────────────────────────────
  {
    sectionLabel: "Home & Work",
    testid: "sidebar-section-home-work",
    groups: [
      {
        label: null,
        testid: "sidebar-group-home",
        items: [
          {
            to: "/",
            icon: LayoutDashboard,
            label: "My Dashboard",
            testid: "sidebar-nav-dashboard",
            end: true,
          },
          {
            icon: ListChecks,
            label: "My Tasks & Approvals",
            testid: "sidebar-nav-tasks-approvals",
            comingSoon: true,
          },
          {
            icon: Calendar,
            label: "My Calendar",
            testid: "sidebar-nav-my-calendar",
            comingSoon: true,
          },
          {
            icon: Bell,
            label: "Inbox & Notifications",
            testid: "sidebar-nav-inbox-notifications",
            comingSoon: true,
          },
          {
            icon: Star,
            label: "Favorites & Recent Work",
            testid: "sidebar-nav-favorites",
            comingSoon: true,
          },
        ],
      },
    ],
  },

  // ── 2. ACADEMICS ──────────────────────────────────────────────────────────
  {
    sectionLabel: "Academics",
    testid: "sidebar-section-academics",
    groups: [
      {
        label: null,
        testid: "sidebar-group-academics-main",
        items: [
          {
            icon: LayoutGrid,
            label: "Academic Overview",
            testid: "sidebar-nav-academic-overview",
            comingSoon: true,
          },
          {
            to: "/academic-structure",
            icon: Network,
            label: "Academic Structure & Programmes",
            testid: "sidebar-nav-programs",
            roles: ["super_admin", "institution_admin", "registrar", "programme_manager"],
          },
          {
            to: "/comply/obe",
            icon: BookOpenCheck,
            label: "Curriculum & Regulations",
            testid: "sidebar-nav-curriculum",
            roles: [
              "super_admin", "institution_admin", "compliance_officer",
              "ai_governance_admin", "faculty", "instructor", "dean", "programme_manager",
            ],
            module: "09.01",
          },
          {
            to: "/learn",
            icon: BookMarked,
            label: "Course Catalogue & Offerings",
            testid: "sidebar-nav-courses",
            module: "04.01",
          },
          {
            icon: CalendarClock,
            label: "Timetable & Scheduling",
            testid: "sidebar-nav-timetable",
            comingSoon: true,
          },
          {
            icon: ContentIcon,
            label: "Teaching & Learning",
            testid: "sidebar-nav-teaching-learning-item",
            comingSoon: true,
          },
          {
            to: "/core/attendance",
            icon: ClipboardCheck,
            label: "Attendance & Engagement",
            testid: "sidebar-nav-attendance",
            roles: [
              "super_admin", "institution_admin", "faculty", "instructor",
              "programme_manager", "registrar", "dean",
            ],
            module: "01.02",
          },
          {
            icon: MessageCircle,
            label: "Academic Advising",
            testid: "sidebar-nav-advising",
            comingSoon: true,
          },
          {
            to: "/content-studio",
            icon: Palette,
            label: "Faculty Teaching Workspace",
            testid: "sidebar-nav-faculty-workspace",
            roles: ["super_admin", "institution_admin", "faculty", "instructor"],
            module: "04.01",
          },
        ],
      },
    ],
  },

  // ── 3. STUDENT LIFECYCLE ──────────────────────────────────────────────────
  {
    sectionLabel: "Student Lifecycle",
    testid: "sidebar-section-student-lifecycle",
    groups: [
      {
        label: null,
        testid: "sidebar-group-student-main",
        items: [
          {
            to: "/core/students",
            icon: Users,
            label: "Student Master & Profiles",
            testid: "sidebar-nav-core-students",
            roles: [
              "super_admin", "institution_admin", "registrar", "programme_manager",
              "faculty", "instructor", "dean",
            ],
            module: "01.02",
          },
          {
            to: "/admissions/applications",
            icon: FileText,
            label: "Admissions & Applications",
            testid: "sidebar-nav-admissions",
            roles: ["super_admin", "institution_admin", "registrar", "programme_manager"],
          },
          {
            to: "/enroll",
            icon: Users2,
            label: "Enrollment & Registration",
            testid: "sidebar-nav-enroll-pipeline",
            roles: [
              "super_admin", "institution_admin", "registrar", "programme_manager",
              "faculty", "instructor", "career_services",
            ],
            module: "01.01",
          },
          {
            to: "/enrollment/offerings",
            icon: Route,
            label: "Academic Progress & Records",
            testid: "sidebar-nav-academic-journey",
            roles: ["super_admin", "institution_admin", "registrar"],
          },
          {
            to: "/academic-standing",
            icon: Award,
            label: "Academic Standing",
            testid: "sidebar-nav-academic-standing",
            roles: [
              "super_admin", "institution_admin", "registrar", "programme_manager",
              "dean", "faculty", "student",
            ],
          },
          {
            icon: HeartHandshake,
            label: "Scholarships & Student Support",
            testid: "sidebar-nav-student-support",
            comingSoon: true,
          },
          {
            icon: Inbox,
            label: "Student Requests & Services",
            testid: "sidebar-nav-student-requests",
            comingSoon: true,
          },
        ],
      },
      {
        label: "Career & Alumni",
        testid: "sidebar-group-career-alumni",
        items: [
          {
            to: "/launch",
            icon: Briefcase,
            label: "Internships & Placements",
            testid: "sidebar-nav-launch-dashboard",
            module: "01.06",
            roles: [
              "super_admin", "institution_admin", "registrar", "career_services",
              "programme_manager", "faculty", "instructor",
            ],
          },
          {
            to: "/alumni-network",
            icon: GraduationCap,
            label: "Graduation & Alumni",
            testid: "sidebar-nav-alumni-network",
            module: "01.06",
            roles: ["super_admin", "institution_admin", "career_services", "executive_leadership"],
          },
          {
            to: "/launch/skills",
            icon: ListChecks,
            label: "My Skills",
            testid: "sidebar-nav-launch-skills",
            roles: ["student"],
            module: "01.06",
          },
          {
            to: "/launch/interview",
            icon: MessageSquareText,
            label: "Mock Interview",
            testid: "sidebar-nav-launch-interview",
            roles: ["student"],
            module: "01.06",
          },
        ],
      },
      {
        label: "Student Self-Service",
        testid: "sidebar-group-student-self-service",
        items: [
          {
            to: "/my-finance",
            icon: Wallet,
            label: "My Finance",
            testid: "sidebar-nav-my-finance-shortcut",
            roles: ["student"],
            module: "01.02",
          },
          {
            to: "/student/invoices",
            icon: FileText,
            label: "My Invoices",
            testid: "sidebar-nav-my-invoices-shortcut",
            roles: ["student"],
            module: "01.02",
          },
          {
            to: "/gradebook",
            icon: BarChart3,
            label: "My Grades",
            testid: "sidebar-nav-my-grades-shortcut",
            roles: ["student"],
          },
          {
            to: "/my-exams",
            icon: ClipboardCheck,
            label: "My Exams",
            testid: "sidebar-nav-my-exams",
            roles: ["student"],
          },
          {
            to: "/my-id",
            icon: CreditCard,
            label: "My Campus ID",
            testid: "sidebar-nav-my-id",
            roles: ["student"],
            module: "08.12",
          },
        ],
      },
    ],
  },

  // ── 4. ASSESSMENT ─────────────────────────────────────────────────────────
  {
    sectionLabel: "Assessment",
    testid: "sidebar-section-assessment",
    groups: [
      {
        label: null,
        testid: "sidebar-group-assessment-main",
        items: [
          {
            icon: BarChart3,
            label: "Assessment Dashboard",
            testid: "sidebar-nav-assessment-dashboard",
            comingSoon: true,
          },
          {
            icon: ScrollText,
            label: "Policies & Schemes",
            testid: "sidebar-nav-assessment-policies",
            comingSoon: true,
          },
          {
            to: "/assessment-design",
            icon: ClipboardList,
            label: "Assessment Blueprint & Design",
            testid: "sidebar-nav-assessment-design",
            roles: [
              "super_admin", "institution_admin", "programme_manager", "dean",
              "faculty", "instructor", "registrar", "assessment_designer", "assessment_reviewer",
            ],
          },
          {
            icon: Layers,
            label: "Question Bank & Rubrics",
            testid: "sidebar-nav-question-bank",
            comingSoon: true,
          },
          {
            icon: CalendarClock,
            label: "Examination Planning",
            testid: "sidebar-nav-examination-planning",
            comingSoon: true,
          },
          {
            to: "/assessment-delivery",
            icon: ClipboardCheck,
            label: "Assessment Delivery",
            testid: "sidebar-nav-assessment-delivery",
            roles: [
              "super_admin", "institution_admin", "registrar", "programme_manager", "dean",
            ],
          },
          {
            to: "/assessment-evaluation",
            icon: UserCheck,
            label: "Evaluation & Moderation",
            testid: "sidebar-nav-assessment-evaluation",
            roles: [
              "super_admin", "institution_admin", "registrar", "programme_manager",
              "dean", "faculty", "instructor",
            ],
          },
          {
            to: "/gradebook",
            icon: BarChart3,
            label: "Results & Grade Processing",
            testid: "sidebar-nav-gradebook",
            roles: [
              "super_admin", "institution_admin", "registrar", "programme_manager",
              "dean", "faculty", "instructor",
            ],
          },
          {
            to: "/assessments",
            icon: Brain,
            label: "Outcomes & Attainment",
            testid: "sidebar-nav-assessments",
            roles: ["super_admin", "institution_admin", "faculty", "instructor", "programme_manager"],
            module: "04.01",
          },
        ],
      },
    ],
  },

  // ── 5. CAMPUS SERVICES ────────────────────────────────────────────────────
  // FROZEN ELEMENTS (AOS-NAV-FREEZE-01 — immutable without superseding ADR):
  //   Group testid:  sidebar-group-campus
  //   Item label:    "Hostels & Residential Life"
  //   Route:         /campus/hostel
  //   Item testid:   sidebar-nav-hostel
  //   Module:        08.02  (canonical M5; legacy alias was HOSTEL — eliminated per AOS-SUB-03)
  {
    sectionLabel: "Campus Services",
    testid: "sidebar-section-campus-services",
    groups: [
      {
        label: null,
        testid: "sidebar-group-campus",
        items: [
          // AOS-CAMPUS-DEMO-02 Phase 3: Campus Overview wired
          {
            to: "/campus/overview",
            icon: Building,
            label: "Campus Overview",
            testid: "sidebar-nav-campus-overview",
          },
          // AOS-LID-01/02: Campus Digital ID & Verification
          {
            to: "/campus/verify",
            icon: ShieldCheck,
            label: "Verify Campus ID",
            testid: "sidebar-nav-campus-verify",
            roles: ["super_admin", "institution_admin", "registrar", "finance_manager", "finance_officer", "programme_manager"],
            module: "08.12",
          },
          // AOS-LID-03: Staff Photo Review Queue
          {
            to: "/campus/photos",
            icon: Camera,
            label: "Photo Review Queue",
            testid: "sidebar-nav-photo-review",
            roles: ["super_admin", "institution_admin", "registrar", "finance_manager", "programme_manager"],
            module: "08.12",
          },
          // FROZEN: AOS-NAV-FREEZE-01 — route, label, testid, module MUST NOT change
          {
            to: "/campus/hostel",
            icon: BedDouble,
            label: "Hostels & Residential Life",
            testid: "sidebar-nav-hostel",
            roles: ["super_admin", "institution_admin", "finance_manager", "registrar"],
            module: "08.02",
          },
          {
            to: "/campus/hostel/gl/journals",
            icon: ScrollText,
            label: "Hostel GL Journals",
            testid: "sidebar-nav-hostel-gl-journals",
            roles: ["super_admin", "institution_admin", "finance_manager", "cfo", "finance_officer", "registrar"],
            module: "08.02",
          },
          {
            to: "/campus/pay/history",
            icon: CreditCard,
            label: "Payment History",
            testid: "sidebar-nav-payment-history",
            roles: ["super_admin", "institution_admin", "finance_manager", "cfo", "finance_officer", "registrar", "student"],
            module: "05.01",
          },
          {
            icon: Building2,
            label: "Facilities & Maintenance",
            testid: "sidebar-nav-facilities",
            comingSoon: true,
          },
          {
            icon: Layers,
            label: "Space & Asset Booking",
            testid: "sidebar-nav-space-booking",
            comingSoon: true,
          },
          {
            icon: Route,
            label: "Transport & Parking",
            testid: "sidebar-nav-transport",
            comingSoon: true,
          },
          {
            icon: ShoppingCart,
            label: "Dining & Meal Plans",
            testid: "sidebar-nav-dining",
            comingSoon: true,
          },
          {
            icon: BookOpen,
            label: "Library Services",
            testid: "sidebar-nav-library",
            comingSoon: true,
          },
          {
            icon: Activity,
            label: "Sports & Recreation",
            testid: "sidebar-nav-sports",
            comingSoon: true,
          },
          {
            icon: Users2,
            label: "Clubs & Student Activities",
            testid: "sidebar-nav-clubs",
            comingSoon: true,
          },
          {
            icon: CalendarClock,
            label: "Campus Events",
            testid: "sidebar-nav-campus-events",
            comingSoon: true,
          },
          {
            icon: HeartHandshake,
            label: "Health & Wellbeing",
            testid: "sidebar-nav-health",
            comingSoon: true,
          },
          {
            to: "/safe",
            icon: ShieldCheck,
            label: "Safety, Security & Access",
            testid: "sidebar-nav-safe",
            module: "08.03",
          },
          {
            icon: Users,
            label: "Visitor & Guest Services",
            testid: "sidebar-nav-visitor-services",
            comingSoon: true,
          },
          {
            to: "/green",
            icon: Leaf,
            label: "Sustainability",
            testid: "sidebar-nav-green",
            roles: ["super_admin", "institution_admin", "executive_leadership"],
            module: "08.03",
          },
        ],
      },
    ],
  },

  // ── 6. OPERATIONS ─────────────────────────────────────────────────────────
  {
    sectionLabel: "Operations",
    testid: "sidebar-section-operations",
    groups: [
      {
        label: null,
        testid: "sidebar-group-ops-main",
        items: [
          {
            icon: BarChart2,
            label: "Operations Dashboard",
            testid: "sidebar-nav-operations-dashboard",
            comingSoon: true,
          },
          {
            icon: Inbox,
            label: "Service Requests & Helpdesk",
            testid: "sidebar-nav-helpdesk",
            comingSoon: true,
          },
          {
            icon: ShoppingCart,
            label: "Procurement & Vendors",
            testid: "sidebar-nav-procurement",
            comingSoon: true,
          },
          {
            icon: Database,
            label: "Inventory & Asset Management",
            testid: "sidebar-nav-inventory",
            comingSoon: true,
          },
          {
            icon: FileStack,
            label: "Contracts & Document Services",
            testid: "sidebar-nav-contracts",
            comingSoon: true,
          },
          {
            icon: MessageCircle,
            label: "Institutional Communications",
            testid: "sidebar-nav-comms",
            comingSoon: true,
          },
          {
            to: "/workflows",
            icon: Workflow,
            label: "Workflow & Approval Operations",
            testid: "sidebar-nav-policies",
            roles: [
              "super_admin", "institution_admin", "registrar", "programme_manager",
              "faculty", "instructor", "compliance_officer", "ai_governance_admin",
              "career_services", "training_manager", "hr_workforce_planner",
              "line_manager", "executive_leadership",
            ],
          },
          {
            icon: SettingsIcon,
            label: "IT Service Operations",
            testid: "sidebar-nav-it-ops",
            comingSoon: true,
          },
        ],
      },
    ],
  },

  // ── 7. FINANCE ────────────────────────────────────────────────────────────
  {
    sectionLabel: "Finance",
    testid: "sidebar-section-finance",
    groups: [
      {
        label: null,
        testid: "sidebar-group-finance-main",
        items: [
          {
            to: "/finance/overview",
            icon: Wallet,
            label: "Finance Overview",
            testid: "sidebar-nav-finance-overview",
            roles: [
              "super_admin", "institution_admin", "registrar", "programme_manager",
              "finance_officer", "finance_manager", "cfo", "dean",
            ],
            module: "01.02",
          },
          {
            to: "/finance/accounts",
            icon: Users,
            label: "Student Fees & Billing",
            testid: "sidebar-nav-finance-accounts",
            roles: [
              "super_admin", "institution_admin", "registrar", "programme_manager",
              "finance_officer", "finance_manager", "cfo",
            ],
            module: "01.02",
          },
          {
            to: "/finance/collections",
            icon: AlertCircle,
            label: "Collections & Receipts",
            testid: "sidebar-nav-collections",
            roles: [
              "finance_manager", "finance_officer", "cfo", "institution_admin", "super_admin",
            ],
            module: "01.02",
          },
          {
            to: "/finance/scholarships-waivers",
            icon: GraduationCap,
            label: "Scholarships & Refunds",
            testid: "sidebar-nav-scholarships-waivers",
            roles: [
              "finance_manager", "finance_officer", "scholarship_manager",
              "cfo", "institution_admin", "super_admin",
            ],
            module: "01.02",
          },
          {
            to: "/finance/invoices",
            icon: FileText,
            label: "Accounts Receivable",
            testid: "sidebar-nav-finance-invoices",
            roles: [
              "super_admin", "institution_admin", "finance_manager",
              "finance_officer", "registrar", "cfo",
            ],
            module: "01.02",
          },
          {
            to: "/finance/reimbursements",
            icon: FileText,
            label: "Accounts Payable",
            testid: "sidebar-nav-reimbursements",
            roles: [
              "finance_manager", "finance_officer", "scholarship_manager",
              "cfo", "institution_admin", "super_admin",
            ],
            module: "01.02",
          },
          {
            icon: ScrollText,
            label: "General Ledger",
            testid: "sidebar-nav-general-ledger",
            comingSoon: true,
          },
          {
            icon: BarChart3,
            label: "Budgets & Cost Centers",
            testid: "sidebar-nav-budgets",
            comingSoon: true,
          },
          {
            to: "/finance/reports",
            icon: BarChart2,
            label: "Financial Reporting",
            testid: "sidebar-nav-finance-reports",
            roles: [
              "finance_manager", "finance_officer", "cfo",
              "institution_admin", "super_admin", "registrar",
            ],
            module: "01.02",
          },
          {
            to: "/my-finance",
            icon: Wallet,
            label: "My Finance",
            testid: "sidebar-nav-my-finance",
            roles: ["student"],
            module: "01.02",
          },
          {
            to: "/student/invoices",
            icon: FileText,
            label: "My Invoices",
            testid: "sidebar-nav-student-invoices",
            roles: ["student"],
            module: "01.02",
          },
        ],
      },
      // Finance Tools — additional operational finance routes (collapsible)
      {
        label: "Finance Tools",
        testid: "sidebar-group-finance-tools",
        collapsible: true,
        defaultOpen: true,
        items: [
          {
            to: "/finance/fee-setup",
            icon: SettingsIcon,
            label: "Fee Setup",
            testid: "sidebar-nav-finance-fee-setup",
            roles: [
              "super_admin", "institution_admin", "registrar", "programme_manager",
              "finance_officer", "finance_manager", "cfo",
            ],
            module: "01.02",
          },
          {
            to: "/finance/assessments",
            icon: BarChart3,
            label: "Finance Assessments",
            testid: "sidebar-nav-finance-assessments",
            roles: ["super_admin", "institution_admin", "finance_manager"],
            module: "01.02",
          },
          {
            to: "/finance/cashier",
            icon: ReceiptText,
            label: "Cashier Desk",
            testid: "sidebar-nav-cashier",
            roles: [
              "finance_manager", "finance_officer", "cfo", "institution_admin", "super_admin",
            ],
            module: "01.02",
          },
          {
            to: "/finance/pos-recon",
            icon: CreditCard,
            label: "POS Reconciliation",
            testid: "sidebar-nav-pos-recon",
            roles: [
              "finance_manager", "finance_officer", "cfo", "institution_admin", "super_admin",
            ],
            module: "01.02",
          },
          {
            to: "/finance/bank-recon",
            icon: Banknote,
            label: "Bank Reconciliation",
            testid: "sidebar-nav-bank-recon",
            roles: [
              "finance_manager", "finance_officer", "cfo", "institution_admin", "super_admin",
            ],
            module: "01.02",
          },
          {
            to: "/finance/approval-queue",
            icon: CheckCircle,
            label: "Approval Queue",
            testid: "sidebar-nav-approval-queue",
            roles: [
              "finance_manager", "scholarship_manager", "cfo", "institution_admin", "super_admin",
            ],
            module: "01.02",
          },
          {
            to: "/finance/sponsorships",
            icon: Building2,
            label: "Sponsorships",
            testid: "sidebar-nav-sponsorships",
            roles: [
              "finance_manager", "finance_officer", "scholarship_manager",
              "cfo", "institution_admin", "super_admin",
            ],
            module: "01.02",
          },
          {
            to: "/finance/refunds",
            icon: Banknote,
            label: "Refunds",
            testid: "sidebar-nav-refunds",
            roles: [
              "finance_manager", "finance_officer", "cfo", "institution_admin", "super_admin",
            ],
            module: "01.02",
          },
          {
            to: "/finance/credits",
            icon: FileText,
            label: "Credits",
            testid: "sidebar-nav-credits",
            roles: [
              "finance_manager", "finance_officer", "cfo", "institution_admin", "super_admin",
            ],
            module: "01.02",
          },
          {
            to: "/ax1-demo",
            icon: Zap,
            label: "AI Remediation Agent",
            testid: "sidebar-nav-ax1-demo",
            roles: ["finance_manager", "cfo", "institution_admin", "super_admin"],
            module: "01.02",
          },
          {
            to: "/finance/admin/scholarships-waivers",
            icon: SettingsIcon,
            label: "Finance Configuration",
            testid: "sidebar-nav-scholarship-admin",
            roles: ["institution_admin", "super_admin"],
            module: "01.02",
          },
        ],
      },
      // COA Studio — Global Chart of Accounts Reference Library (AOS-FIN-COA-GLOBAL-01)
      {
        label: "COA Studio",
        testid: "sidebar-group-coa-studio",
        collapsible: true,
        defaultOpen: true,
        items: [
          {
            to: "/finance/coa/reference",
            icon: BookOpen,
            label: "COA Reference Library",
            testid: "sidebar-nav-coa-reference",
            roles: [
              "super_admin", "institution_admin", "cfo",
              "finance_manager", "finance_officer", "registrar",
            ],
            // No module restriction — COA is a platform-global resource.
          },
          {
            to: "/finance/coa/activations",
            icon: CheckSquare,
            label: "Account Activation",
            testid: "sidebar-nav-coa-activations",
            roles: [
              "super_admin", "institution_admin", "cfo",
              "finance_manager", "finance_officer", "registrar",
            ],
          },
          {
            to: "/finance/coa/segments",
            icon: Layers,
            label: "Segment Config",
            testid: "sidebar-nav-coa-segments",
            roles: ["super_admin", "institution_admin", "cfo"],
          },
          {
            to: "/finance/coa/combinations",
            icon: ShieldCheck,
            label: "Account Combinations",
            testid: "sidebar-nav-coa-combinations",
            roles: ["super_admin", "institution_admin", "cfo"],
          },
        ],
      },
    ],
  },

  // ── 8. PEOPLE ─────────────────────────────────────────────────────────────
  {
    sectionLabel: "People",
    testid: "sidebar-section-people",
    groups: [
      {
        label: null,
        testid: "sidebar-group-people-main",
        items: [
          {
            icon: Users,
            label: "People Overview",
            testid: "sidebar-nav-people-overview",
            comingSoon: true,
          },
          {
            to: "/people",
            icon: GraduationCap,
            label: "Faculty & Staff Directory",
            testid: "sidebar-nav-faculty",
            roles: [
              "super_admin", "institution_admin", "faculty", "instructor",
              "hr_workforce_planner", "executive_leadership",
            ],
            module: "06.01",
          },
          {
            icon: UserCheck,
            label: "Recruitment & Onboarding",
            testid: "sidebar-nav-recruitment",
            comingSoon: true,
          },
          {
            icon: FileStack,
            label: "Employment & Contracts",
            to: "/people/employment",
            testid: "sidebar-nav-employment-contracts",
            roles: [
              "super_admin", "institution_admin", "hr_admin", "registrar",
              "dean", "department_head", "finance_manager", "cfo",
              "payroll_officer",
            ],
          },
          {
            icon: ClipboardCheck,
            label: "Attendance & Leave",
            to: "/people/attendance-leave",
            testid: "sidebar-nav-hr-attendance",
            roles: [
              "super_admin", "institution_admin", "hr_admin", "registrar",
              "dean", "department_head", "faculty", "instructor",
            ],
          },
          {
            icon: ListChecks,
            label: "Workload & Assignments",
            to: "/people/workload",
            testid: "sidebar-nav-workload",
            roles: [
              "super_admin", "institution_admin", "hr_admin", "registrar",
              "dean", "department_head", "faculty", "instructor",
            ],
          },
          // ── PEO-03 ────────────────────────────────────────────────────────
          {
            icon: Inbox,
            label: "Pending Approvals",
            to: "/people/approvals",
            testid: "sidebar-nav-pending-approvals",
            badgeKey: "pending_leave_approvals",
            roles: [
              "super_admin", "institution_admin", "hr_admin",
              "dean", "department_head",
            ],
          },
          {
            icon: Link2,
            label: "Identity Management",
            to: "/people/identity",
            testid: "sidebar-nav-identity-management",
            badgeKey: "missing_identity_links",
            roles: ["super_admin", "institution_admin", "hr_admin"],
          },
          {
            icon: UserCheck,
            label: "My Self-Service",
            to: "/people/self-service",
            testid: "sidebar-nav-self-service",
            roles: [
              "faculty", "instructor", "hr_admin",
              "institution_admin", "dean", "department_head",
              "registrar", "super_admin",
            ],
          },
          // ─────────────────────────────────────────────────────────────────
          {
            icon: Banknote,
            label: "Payroll & Benefits",
            testid: "sidebar-nav-payroll",
            comingSoon: true,
          },
          {
            icon: TrendingUp,
            label: "Performance & Development",
            testid: "sidebar-nav-performance-dev",
            comingSoon: true,
          },
          {
            icon: Inbox,
            label: "Employee Self-Service",
            testid: "sidebar-nav-employee-self-service",
            comingSoon: true,
          },
        ],
      },
    ],
  },

  // ── 9. RESEARCH ───────────────────────────────────────────────────────────
  {
    sectionLabel: "Research",
    testid: "sidebar-section-research",
    groups: [
      {
        label: null,
        testid: "sidebar-group-research-main",
        items: [
          {
            icon: FlaskConical,
            label: "Research Overview",
            testid: "sidebar-nav-research-overview",
            comingSoon: true,
          },
          {
            icon: FileStack,
            label: "Projects & Proposals",
            testid: "sidebar-nav-research-projects",
            comingSoon: true,
          },
          {
            icon: Banknote,
            label: "Grants & Funding",
            testid: "sidebar-nav-grants-funding",
            comingSoon: true,
          },
          {
            icon: ShieldCheck,
            label: "Ethics & Approvals",
            testid: "sidebar-nav-research-ethics",
            comingSoon: true,
          },
          {
            icon: BookOpen,
            label: "Publications & Outputs",
            testid: "sidebar-nav-publications",
            comingSoon: true,
          },
          {
            icon: FlaskConical,
            label: "Research Assets & Laboratories",
            testid: "sidebar-nav-research-labs",
            comingSoon: true,
          },
          {
            icon: Link2,
            label: "Collaborations & Innovation",
            testid: "sidebar-nav-collaborations",
            comingSoon: true,
          },
        ],
      },
    ],
  },

  // ── 10. QUALITY ───────────────────────────────────────────────────────────
  {
    sectionLabel: "Quality",
    testid: "sidebar-section-quality",
    groups: [
      {
        label: null,
        testid: "sidebar-group-quality-main",
        items: [
          {
            icon: BarChart3,
            label: "Quality Dashboard",
            testid: "sidebar-nav-quality-dashboard",
            comingSoon: true,
          },
          {
            to: "/comply",
            icon: ShieldCheck,
            label: "Accreditation & Compliance",
            testid: "sidebar-nav-policy-risk",
            roles: [
              "super_admin", "institution_admin", "compliance_officer", "programme_manager",
            ],
            module: "09.01",
          },
          {
            to: "/governance",
            icon: Scale,
            label: "AI Governance",
            testid: "sidebar-nav-ai-governance",
            roles: [
              "super_admin", "institution_admin", "ai_governance_admin", "compliance_officer",
            ],
          },
          {
            icon: Target,
            label: "Institutional Outcomes",
            testid: "sidebar-nav-institutional-outcomes",
            comingSoon: true,
          },
          {
            icon: BookOpenCheck,
            label: "Programme Review",
            testid: "sidebar-nav-programme-review",
            comingSoon: true,
          },
          {
            icon: MessageCircle,
            label: "Surveys & Feedback",
            testid: "sidebar-nav-surveys",
            comingSoon: true,
          },
          {
            to: "/ai-use-cases",
            icon: FileSearch,
            label: "Audits & Evidence",
            testid: "sidebar-nav-evidence",
            roles: [
              "super_admin", "institution_admin", "ai_governance_admin", "compliance_officer",
            ],
          },
          {
            to: "/analytics",
            icon: ClipboardCheck,
            label: "Assurance",
            testid: "sidebar-nav-assurance",
            roles: [
              "super_admin", "institution_admin", "executive_leadership",
              "compliance_officer", "ai_governance_admin",
            ],
            module: "10.03",
          },
          {
            icon: CheckCircle,
            label: "Improvement Action Plans",
            testid: "sidebar-nav-improvement-plans",
            comingSoon: true,
          },
          {
            icon: ScrollText,
            label: "Regulatory Reporting",
            testid: "sidebar-nav-regulatory-reporting",
            comingSoon: true,
          },
        ],
      },
    ],
  },

  // ── 11. INTELLIGENCE ──────────────────────────────────────────────────────
  {
    sectionLabel: "Intelligence",
    testid: "sidebar-section-intelligence",
    groups: [
      {
        label: "Analytics",
        testid: "sidebar-group-analytics",
        items: [
          {
            to: "/insights",
            icon: Activity,
            label: "Institutional Dashboard",
            testid: "sidebar-nav-insights",
            roles: ["super_admin", "institution_admin", "executive_leadership"],
            module: "10.03",
          },
          {
            to: "/exec-briefing",
            icon: TrendingUp,
            label: "Academic Performance",
            testid: "sidebar-nav-exec-briefing",
            roles: ["super_admin", "institution_admin", "executive_leadership"],
          },
          {
            icon: Users,
            label: "Student Success & Retention",
            testid: "sidebar-nav-student-success-analytics",
            comingSoon: true,
          },
          {
            icon: Wallet,
            label: "Finance Analytics",
            testid: "sidebar-nav-finance-analytics",
            comingSoon: true,
          },
          {
            icon: Building2,
            label: "Campus & Residential Analytics",
            testid: "sidebar-nav-campus-analytics",
            comingSoon: true,
          },
          {
            icon: Users2,
            label: "Faculty & Workforce Analytics",
            testid: "sidebar-nav-workforce-analytics",
            comingSoon: true,
          },
          {
            icon: ShieldCheck,
            label: "Quality & Accreditation Insights",
            testid: "sidebar-nav-quality-insights",
            comingSoon: true,
          },
        ],
      },
      {
        label: "AI & Machine Intelligence",
        testid: "sidebar-group-ai-intelligence",
        items: [
          {
            to: "/ai",
            icon: Sparkles,
            label: "Academia AI",
            testid: "sidebar-nav-academia-ai",
            module: "10.05",
          },
          {
            to: "/student-assistant",
            icon: MessageSquareText,
            label: "Student Assistant",
            testid: "sidebar-nav-student-assistant",
            module: "01.06",
          },
          {
            to: "/ai/knowledge",
            icon: BookOpen,
            label: "Knowledge Hub",
            testid: "sidebar-nav-knowledge-hub",
            roles: [
              "super_admin", "institution_admin", "faculty", "instructor",
              "registrar", "programme_manager", "compliance_officer", "ai_governance_admin",
            ],
            module: "10.05",
          },
          {
            to: "/ax1-demo",
            icon: Bot,
            label: "Agents",
            testid: "sidebar-nav-agents",
            roles: ["finance_manager", "cfo", "institution_admin", "super_admin"],
          },
        ],
      },
    ],
  },

  // ── 12. ADMINISTRATION ────────────────────────────────────────────────────
  {
    sectionLabel: "Administration",
    testid: "sidebar-section-administration",
    groups: [
      {
        label: "Institution",
        testid: "sidebar-group-institution",
        items: [
          {
            to: "/institution-setup",
            icon: Building2,
            label: "Institution & Organization Setup",
            testid: "sidebar-nav-institution-setup",
            roles: ["super_admin", "institution_admin"],
          },
          {
            to: "/setup",
            icon: SlidersHorizontal,
            label: "Setup Studio",
            testid: "sidebar-nav-setup-studio",
            roles: ["super_admin", "institution_admin", "dean", "registrar"],
          },
          {
            to: "/admin/academic-setup",
            icon: BookOpen,
            label: "Academic Configuration",
            testid: "sidebar-nav-academic-setup",
            roles: ["super_admin", "institution_admin", "admin", "registrar"],
          },
          {
            to: "/users-roles",
            icon: Users,
            label: "User, Role & Permission Management",
            testid: "sidebar-nav-users-roles",
            roles: ["super_admin", "institution_admin"],
          },
        ],
      },
      {
        label: "Platform",
        testid: "sidebar-group-platform-main",
        items: [
          {
            to: "/admin/integrations",
            icon: Plug,
            label: "Module Activation & Entitlements",
            testid: "sidebar-nav-integrations",
            roles: ["super_admin", "institution_admin"],
            module: "01.02",
          },
          {
            to: "/settings",
            icon: SettingsIcon,
            label: "Policies & Workflow Rules",
            testid: "sidebar-nav-settings",
            roles: [
              "super_admin", "institution_admin", "registrar", "programme_manager",
              "faculty", "instructor", "compliance_officer", "ai_governance_admin",
              "career_services", "training_manager", "hr_workforce_planner",
              "line_manager", "executive_leadership",
            ],
          },
          {
            to: "/finance/integrations/control-center",
            icon: Plug,
            label: "Integration Control Center",
            testid: "sidebar-nav-integration-center",
            roles: ["super_admin", "institution_admin", "finance_manager", "cfo"],
            module: "05.01",
          },
          {
            icon: Database,
            label: "Data & Migration Operations",
            testid: "sidebar-nav-data-ops",
            comingSoon: true,
          },
          {
            to: "/setup/gates",
            icon: ShieldCheck,
            label: "System Health & Configuration",
            testid: "sidebar-nav-readiness-gates",
            roles: ["super_admin", "institution_admin", "registrar"],
          },
          {
            icon: Workflow,
            label: "Workflow Studio",
            testid: "sidebar-nav-workflow-studio",
            comingSoon: true,
          },
          {
            icon: Bot,
            label: "Agent Studio",
            testid: "sidebar-nav-agent-studio",
            comingSoon: true,
          },
        ],
      },
      // Onboarding & Config — collapsible (less frequently used)
      {
        label: "Onboarding & Config",
        testid: "sidebar-group-admin-config",
        collapsible: true,
        defaultOpen: false,
        items: [
          {
            to: "/admin/tenant-config",
            icon: Palette,
            label: "Branding",
            testid: "sidebar-nav-tenant-config",
            roles: ["super_admin", "institution_admin"],
          },
          {
            to: "/admin/institution-onboarding",
            icon: Building2,
            label: "Institution Onboarding",
            testid: "sidebar-nav-institution-onboarding",
            roles: ["super_admin"],
          },
          {
            to: "/onboarding",
            icon: Rocket,
            label: "Onboarding Wizard",
            testid: "sidebar-nav-onboarding",
            roles: ["super_admin", "institution_admin"],
          },
          {
            to: "/setup/uat-inbox",
            icon: Inbox,
            label: "UAT Inbox",
            testid: "sidebar-nav-uat-inbox",
            roles: ["super_admin", "institution_admin", "registrar"],
          },
          {
            to: "/enrollment/import",
            icon: Upload,
            label: "Import Students",
            testid: "sidebar-nav-enrollment-import",
            roles: ["super_admin", "institution_admin", "registrar"],
          },
          {
            to: "/admin-guide",
            icon: BookOpen,
            label: "Admin Guide",
            testid: "sidebar-nav-admin-guide",
            roles: ["super_admin", "institution_admin"],
          },
          {
            to: "/product-brief",
            icon: ScrollText,
            label: "Product Brief",
            testid: "sidebar-nav-product-brief",
            roles: ["super_admin", "institution_admin"],
          },
        ],
      },
    ],
  },

  // ── HIDDEN ────────────────────────────────────────────────────────────────
  // NAV-INV-008: Statutory Config MUST NOT appear in user-facing nav.
  // NAV-INV-009: ARGOV remains hidden from sidebar (surfaced via ARGOVBadge only).
  // Routes and components preserved; sidebar entries NOT rendered.
];

// ─────────────────────────────────────────────────────────────────────────────
// SIDEBAR COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function Sidebar({ isOpen = false, onClose = () => {} }) {
  const { config: tenantConfig } = useTenantConfig();
  const { current } = useInstitution();
  const { user } = useAuth();
  const { statusOf } = useTenantModules();

  // All top-level domains open by default; user can collapse any
  const [openSections, setOpenSections] = useState(() =>
    new Set(NAV_SECTIONS.map((s) => s.testid))
  );
  const toggleSection = (testid) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(testid)) next.delete(testid);
      else next.add(testid);
      return next;
    });

  // Track collapsible sub-groups; all start closed
  const [openGroups, setOpenGroups] = useState(() => new Set([]));
  const toggleGroup = (testid) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(testid)) next.delete(testid);
      else next.add(testid);
      return next;
    });

  // PEO-03: Exception badge counts for sidebar indicators
  const [peo03Badges, setPeo03Badges] = useState({});
  useEffect(() => {
    const token = localStorage.getItem("academiaos_token");
    if (!token || !user) return;
    const canSee = [
      "super_admin", "institution_admin", "hr_admin", "dean", "department_head",
    ].includes(user?.role);
    if (!canSee) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    fetch(`${backendUrl}/api/v1/peo03/exceptions/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data) setPeo03Badges(data); })
      .catch(() => {});
  }, [user]);

  const visibleSections = NAV_SECTIONS
    .map((section) => ({
      ...section,
      groups: section.groups
        .map((group) => ({
          ...group,
          items: group.items
            .filter((item) => {
              if (item.comingSoon && !item.to) {
                if (item.roles && !item.roles.includes(user?.role)) return false;
                return true;
              }
              if (item.roles && !item.roles.includes(user?.role)) return false;
              if (item.module && statusOf(item.module) === "disabled") return false;
              return true;
            })
            .map((item) => ({
              ...item,
              _moduleStatus: item.comingSoon
                ? "coming_soon"
                : item.module
                ? statusOf(item.module)
                : "active",
            })),
        }))
        .filter((group) => group.items.length > 0),
    }))
    .filter((section) => section.groups.length > 0);

  return (
    <aside
      className={`sidebar-rail flex flex-col ${isOpen ? "is-open" : ""}`}
      data-testid="sidebar"
    >
      {/* ── Brand header ─────────────────────────────────────────────────── */}
      <div
        className="flex h-[72px] items-center gap-3 px-5 border-b"
        data-testid="sidebar-brand"
      >
        {current?.logo_url ? (
          <img
            src={current.logo_url}
            alt={current.short_name || "Institution logo"}
            className="h-9 w-9 rounded-md object-contain bg-muted/20 border border-border p-0.5"
            data-testid="sidebar-tenant-logo"
          />
        ) : (
          <div
            className="flex h-9 w-9 items-center justify-center rounded-lg text-sm font-bold shrink-0"
            style={{
              fontFamily: "var(--font-heading)",
              letterSpacing: "-0.02em",
            }}
            data-testid="sidebar-brand-mark"
          >
            {current?.short_name?.[0] || "A"}
          </div>
        )}
        <div className="leading-tight flex-1 min-w-0">
          <div
            className="text-sm font-semibold tracking-tight truncate"
            data-testid="sidebar-tenant-name"
          >
            {tenantConfig?.platform_display_name || current?.short_name || "AcademiaOS"}
          </div>
          {(tenantConfig?.powered_by_label ?? "Powered by AcademiaOS") && (
            <div
              className="text-[10px] uppercase tracking-[0.18em]"
              data-testid="sidebar-powered-by"
            >
              {tenantConfig?.powered_by_label ?? "Powered by AcademiaOS"}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          className="md:hidden rounded-md p-1.5 transition-colors"
          aria-label="Close menu"
          data-testid="mobile-menu-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ── Navigation ───────────────────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto px-3 py-3" data-testid="sidebar-nav">
        {visibleSections.map((section, sIdx) => {
          const isSectionOpen = openSections.has(section.testid);

          return (
            <div key={section.testid} data-testid={section.testid}>
              {/* Domain heading — collapsible toggle */}
              <button
                onClick={() => toggleSection(section.testid)}
                data-testid={`${section.testid}-toggle`}
                className={[
                  "w-full flex items-center justify-between px-3 pb-1.5",
                  "label-eyebrow aos-sidebar-section-toggle transition-colors",
                  sIdx === 0 ? "pt-2" : "pt-4 mt-1 border-t border-border",
                ].join(" ")}
              >
                <span>{section.sectionLabel}</span>
                {isSectionOpen ? (
                  <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
                ) : (
                  <ChevronRight className="h-3 w-3 opacity-50 shrink-0" />
                )}
              </button>

              {isSectionOpen && (
                <div className="space-y-2 mb-1">
                  {section.groups.map((group) => {
                    const isCollapsible = Boolean(group.collapsible);
                    const isGroupOpen = openGroups.has(group.testid);

                    return (
                      <div key={group.testid} data-testid={group.testid}>
                        {/* Sub-group label — plain or collapsible */}
                        {group.label && !isCollapsible && (
                          <div className="px-3 pt-1.5 pb-0.5 text-[9px] font-semibold uppercase tracking-widest aos-sidebar-group-label">
                            {group.label}
                          </div>
                        )}
                        {group.label && isCollapsible && (
                          <button
                            onClick={() => toggleGroup(group.testid)}
                            data-testid={`${group.testid}-toggle`}
                            className="w-full flex items-center justify-between px-3 pt-2 pb-0.5 text-[9px] font-semibold uppercase tracking-widest aos-sidebar-group-toggle transition-colors group"
                          >
                            <span>{group.label}</span>
                            {isGroupOpen ? (
                              <ChevronDown className="h-3 w-3 opacity-50 group-hover:opacity-80" />
                            ) : (
                              <ChevronRight className="h-3 w-3 opacity-50 group-hover:opacity-80" />
                            )}
                          </button>
                        )}

                        {/* Items — always visible for non-collapsible groups */}
                        {(!isCollapsible || isGroupOpen) && (
                          <ul className="space-y-0.5">
                            {group.items.map((item) => {
                              const isSoon = item._moduleStatus === "coming_soon";
                              const hasRoute = Boolean(item.to);

                              if (isSoon && !hasRoute) {
                                return (
                                  <li key={item.testid}>
                                    <div
                                      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm cursor-default select-none aos-sidebar-nav-item-soon"
                                      data-testid={item.testid}
                                    >
                                      <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                                      <span className="truncate flex-1">{item.label}</span>
                                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded aos-sidebar-soon-badge">
                                        Soon
                                      </span>
                                    </div>
                                  </li>
                                );
                              }

                              return (
                                <li key={item.to}>
                                  <NavLink
                                    to={item.to}
                                    end={item.end}
                                    data-testid={item.testid}
                                    className={({ isActive }) =>
                                      [
                                        "group flex items-center gap-3 rounded-md px-3 py-2 text-sm",
                                        isActive
                                          ? "aos-sidebar-nav-item-active"
                                          : "aos-sidebar-nav-item",
                                      ].join(" ")
                                    }
                                  >
                                    {({ isActive }) => (
                                      <>
                                        <item.icon
                                          className="h-4 w-4 shrink-0"
                                          strokeWidth={isActive ? 2.25 : 1.75}
                                        />
                                        <span className="truncate flex-1">{item.label}</span>
                                        {isSoon && (
                                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded aos-sidebar-soon-badge">
                                            Soon
                                          </span>
                                        )}
                                        {item.badgeKey && peo03Badges[item.badgeKey] > 0 && (
                                          <span
                                            className="ml-auto min-w-[1.125rem] h-[1.125rem] rounded-full bg-red-400 text-white text-[9px] font-bold flex items-center justify-center px-1 shrink-0"
                                            data-testid={`badge-${item.testid}`}
                                          >
                                            {peo03Badges[item.badgeKey] > 99 ? "99+" : peo03Badges[item.badgeKey]}
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </NavLink>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <div className="sidebar-footer px-5 py-3 text-[11px]">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" />
          <span data-testid="sidebar-footer-governance">AI Governance · Active</span>
        </div>
      </div>
    </aside>
  );
}
